// src/utils/registrationHandoff.js
// ============================================================================
// REGISTRATION HANDOFF — one credential, in memory, for one navigation
// ----------------------------------------------------------------------------
// Carries what LoginForm already has in its own state to Registration, across a
// single client-side route change, when someone signs in against a verified
// account that never got a company.
//
// WHY THIS EXISTS AT ALL
//
// That sign-in is refused with a 403 and no session (authController: a member
// with no company has no tier and no capabilities, so letting them in lands
// them on a dead workspace). But finishing their registration means creating a
// company and then going to Stripe checkout — and checkout needs a session.
// The proven path does this by calling login() with the password captured
// during signup. The resume path has no such password unless something carries
// it, and without it the user is stranded on the free tier with no way to pay.
//
// WHY NOT ROUTER STATE
//
// React Router state is written to history.state, which browsers SERIALIZE TO
// DISK for session restore. A password put there outlives the tab and the
// browser process. Identity (memberId, email, name) travels that way and is
// fine; a password must not.
//
// A module variable, by contrast, lives in the JS heap of one page load.
// navigate() is a client-side route change — no reload — so it survives the
// hop to /register and nothing else. A refresh, a new tab, or a restored
// session all lose it, which is the correct outcome: those cases fall back to
// asking the user to sign in once, and Registration is written to handle that.
//
// IS THIS MORE EXPOSED THAN WHAT ALREADY HAPPENS? No. The ordinary signup flow
// holds the same password in Registration's `formData.password` React state for
// the entire multi-step form — minutes, typically. This holds it for the
// hundreds of milliseconds between two components, and clears it on read.
//
// THE RULES, WHICH THE IMPLEMENTATION ENFORCES RATHER THAN DOCUMENTS
//   • take() CLEARS. There is no repeatable read; a second caller gets null.
//   • It expires. Left untaken, it is unreadable after TTL_MS.
//   • It is keyed to a memberId. A stale entry cannot be applied to a
//     different member, which is the failure that would matter.
//   • It never touches localStorage, sessionStorage, cookies or the URL.
//   • It is never logged. Do not add a console.log to this file.
// ============================================================================

// Long enough to cover a slow company-creation transaction (the template copy
// alone runs ~3s) plus a person reading the form. Short enough that an
// abandoned tab is not holding a credential for the afternoon. The realistic
// window is one to two minutes; this is generous, not a target.
const TTL_MS = 10 * 60 * 1000;

let pending = null;   // { memberId, email, firstName, lastName, password }
let expiresAt = 0;
let expiryTimer = null;

/** Drop it and cancel the timer. Safe to call at any time, repeatedly. */
export function clearRegistrationHandoff() {
  pending = null;
  expiresAt = 0;
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
}

/**
 * Stash the handoff. Replaces any previous one — a second sign-in attempt
 * supersedes the first, and leaving the older credential around would be worse
 * than pointless.
 *
 * @param {{memberId:number|string, email?:string, firstName?:string,
 *          lastName?:string, password?:string}} data
 */
export function setRegistrationHandoff(data) {
  clearRegistrationHandoff();
  if (!data || data.memberId == null) return;

  pending = {
    memberId: data.memberId,
    email: data.email || '',
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    password: data.password || '',
  };
  expiresAt = Date.now() + TTL_MS;

  // Belt and braces alongside the expiry check in take(). The check alone would
  // leave the credential sitting in the heap of an abandoned tab until
  // something happened to read it — which, in an abandoned tab, is never.
  expiryTimer = setTimeout(clearRegistrationHandoff, TTL_MS);
}

/**
 * Read it ONCE and clear it.
 *
 * @param {number|string} [expectedMemberId] when given, the stash must belong
 *        to this member or nothing is returned. The caller knows who it is
 *        resuming (from router state, which survives a refresh); this stops a
 *        leftover entry from one member being applied to another.
 * @returns {{memberId, email, firstName, lastName, password}|null}
 */
export function takeRegistrationHandoff(expectedMemberId) {
  const held = pending;
  const valid = !!held && Date.now() < expiresAt;

  // Cleared on EVERY call, hit or miss. A read that fails the member check or
  // the expiry check still consumes the stash — if it was not usable now it
  // will not become usable later, and keeping it only extends its life.
  clearRegistrationHandoff();

  if (!valid) return null;
  if (
    expectedMemberId != null &&
    String(held.memberId) !== String(expectedMemberId)
  ) {
    return null;
  }
  return held;
}

/**
 * Is a usable handoff waiting for this member? Does NOT consume it.
 *
 * For deciding what to render before committing to reading — the read has to
 * happen where the value is used, and doing it early to answer a question would
 * throw the credential away.
 */
export function hasRegistrationHandoff(expectedMemberId) {
  if (!pending || Date.now() >= expiresAt) return false;
  if (expectedMemberId == null) return true;
  return String(pending.memberId) === String(expectedMemberId);
}